import React from "react";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/solid";
import LoadingSpinner from "../core/LoadingSpinner";
import SectionLoading from "../core/SectionLoading";

/**
 * Props for AccordionSection component
 */
export interface AccordionSectionProps {
  id: string;
  title: string;
  icon: React.ReactNode;
  preview: string;
  children: React.ReactNode;
  isExpanded: boolean;
  isLoading: boolean;
  onToggle: (id: string) => void;
  disabled?: boolean; // When true, disables form inputs inside the accordion but keeps header clickable
  /** Optional pill/badge rendered inline next to the title (e.g. green
   *  "active" indicator). Visible in both collapsed and expanded states. */
  badge?: React.ReactNode;
}

/**
 * Reusable accordion section component
 * Used throughout the meeting configuration form for organizing settings
 */
const AccordionSection: React.FC<AccordionSectionProps> = React.memo(({
  id,
  title,
  icon,
  preview,
  children,
  isExpanded,
  isLoading,
  onToggle,
  disabled = false,
  badge,
}) => (
  <div className="border border-gray-200 rounded-lg accordion-section overflow-hidden">
    <button
      type="button"
      className="w-full px-4 py-4 bg-white focus:outline-none accordion-section rounded-t-lg"
      onClick={() => onToggle(id)}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0 text-gray-400">{icon}</div>
          <div className="text-left">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-medium text-gray-900">{title}</h3>
              {badge}
            </div>
            <p className="text-sm text-gray-500 mt-1">{preview}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {isLoading && <LoadingSpinner size="sm" color="primary" />}
          <div className="transition-transform duration-150 ease-out">
            {isExpanded ? (
              <ChevronDownIcon className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronRightIcon className="h-5 w-5 text-gray-400" />
            )}
          </div>
        </div>
      </div>
    </button>

    {isExpanded && (
      <div className={`border-t border-gray-200 px-4 py-4 bg-gray-50 accordion-content entering ${disabled ? 'opacity-60' : ''}`}>
        <SectionLoading isLoading={isLoading} loadingText="Loading section...">
          {disabled ? (
            <fieldset disabled className="contents">
              {children}
            </fieldset>
          ) : (
            children
          )}
        </SectionLoading>
      </div>
    )}
  </div>
));

AccordionSection.displayName = 'AccordionSection';

export default AccordionSection;
